import { Check, Copy, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type {
  ApiKeyAutoModelNamespaceRecord,
  ApiKeyModelMappingConflict,
  ApiKeyModelMappingRecord,
  ApiKeySummary,
  CatalogSku,
  CreateApiKeyRequest,
  UpdateApiKeyRequest,
} from '@nadoutong/contracts';
import { getGatewayBaseUrl, PlatformApiError } from '../api/client.ts';
import { ModelMappingConflictError } from '../api/keyModelMappings.ts';
import { useDisplayRate } from '../api/useBilling.ts';
import { useCatalog } from '../api/useCatalog.ts';
import {
  useApplyKeyModelNamespace,
  useDeleteKeyModelMapping,
  useDisableKeyAutoModelNamespace,
  useEnableKeyAutoModelNamespace,
  useKeyModelMappings,
  useUpsertKeyModelMapping,
} from '../api/useKeyModelMappings.ts';
import { useCreateApiKey, useKeysList, useRevokeApiKey, useUpdateApiKey } from '../api/useKeys.ts';
import { Modal } from '../components/Modal.tsx';
import { Skeleton } from '../components/Skeleton.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import {
  formatTimestamp,
  formatUsdCentsAsCny,
  usdCentsToCnyCents,
} from '../components/formatters.ts';
import { useClipboard } from '../hooks/useClipboard.tsx';
import { hasPermission, useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { Button, Select, TBody, Table, Td, Th, THead, Tr } from '../ui/index.ts';


const INPUT_CLASS =
  'h-9 w-full rounded border border-line bg-raised px-3 text-body text-ink placeholder:text-ink-faint transition-colors duration-150 hover:border-line-strong focus:border-forest focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/30 disabled:pointer-events-none disabled:opacity-60';

function isValidDisplayRate(cnyPerUsd: number | null | undefined): cnyPerUsd is number {
  return typeof cnyPerUsd === 'number' && Number.isFinite(cnyPerUsd) && cnyPerUsd > 0;
}

function formatLimitInputValue(
  usdCents: number | null | undefined,
  cnyPerUsd: number | null | undefined,
): string {
  if (typeof usdCents !== 'number' || !Number.isFinite(usdCents)) return '';
  const cnyCents = usdCentsToCnyCents(usdCents, cnyPerUsd);
  if (cnyCents === null) return '';
  const yuan = (cnyCents / 100).toFixed(2);
  return yuan.endsWith('.00') ? yuan.slice(0, -3) : yuan;
}

function parseMonthlyLimitInput(
  raw: string,
  cnyPerUsd: number | null | undefined,
  t: ReturnType<typeof useT>,
): { ok: true; value: number | null } | { ok: false; message: string } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { ok: true, value: null };
  }
  if (!/^\d+(?:\.\d{1,2})?$/.test(trimmed)) {
    return { ok: false, message: t('keys.limit_error.invalid') };
  }
  if (!isValidDisplayRate(cnyPerUsd)) {
    return { ok: false, message: t('keys.limit_error.rate_unavailable') };
  }
  const parts = trimmed.split('.');
  const wholeRaw = parts[0] ?? '0';
  const fractionRaw = parts[1] ?? '';
  const cnyCents =
    Number.parseInt(wholeRaw, 10) * 100 +
    Number.parseInt(fractionRaw.padEnd(2, '0'), 10);
  if (!Number.isFinite(cnyCents) || cnyCents < 0) {
    return { ok: false, message: t('keys.limit_error.invalid') };
  }
  const usdCents = Math.round(cnyCents / cnyPerUsd);
  // #1620: the server refuses a 0 cap — it reads as "no ceiling" to the rating
  // engine and as "hard stop" to the gate it replaced. Check the CONVERTED
  // value: a small enough RMB amount rounds down to 0 USD cents, so a "0.01"
  // would otherwise pass here and come back a 400.
  if (usdCents === 0) {
    return { ok: false, message: t('keys.limit_error.zero') };
  }
  return { ok: true, value: usdCents };
}

/** Namespace-grouped view over the marketplace SKUs (epic #898 P6 #913). */
interface SkuNamespaceGroup {
  readonly namespaceId: string;
  readonly displayName: string;
  readonly skus: readonly CatalogSku[];
}

function groupSkusByNamespace(skus: readonly CatalogSku[]): readonly SkuNamespaceGroup[] {
  const groups = new Map<string, { displayName: string; skus: CatalogSku[] }>();
  for (const sku of skus) {
    const group = groups.get(sku.namespace_id) ?? {
      displayName: sku.namespace_display_name,
      skus: [],
    };
    group.skus.push(sku);
    groups.set(sku.namespace_id, group);
  }
  return [...groups.entries()]
    .map(([namespaceId, group]) => ({
      namespaceId,
      displayName: group.displayName,
      skus: [...group.skus].sort((a, b) => a.model.localeCompare(b.model)),
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/**
 * API key management surface.
 *
 * Layout:
 *   - Header with "Create key" button.
 *   - Table of existing keys (prefix + name + monthly cap + timestamps +
 *     inline rename + mappings + delete). User-facing history stays
 *     simple: revoked rows are treated as deleted and hidden.
 *   - Modal that surfaces the freshly-created plaintext key ONCE.
 *
 * Channel-less since epic #898 P6 (#913): keys no longer bind to a
 * channel. Model routing is per-key alias → SKU mappings managed in the
 * "Mappings" drawer, and creation can optionally seed those mappings
 * through one atomic server-side namespace snapshot. The key POST body
 * never carries channel_id.
 */

export function Keys(): JSX.Element {
  const t = useT();
  const { user } = useSession();
  const keysQ = useKeysList();
  const activeKeys = useMemo(
    () => keysQ.data?.keys.filter((key) => key.revokedAt === null) ?? [],
    [keysQ.data],
  );
  const createM = useCreateApiKey();
  const applyNamespaceM = useApplyKeyModelNamespace();
  const toast = useToast();
  const canManageKeys =
    hasPermission(user, 'org:api_keys:manage') ||
    hasPermission(user, 'member:api_keys:manage');
  const [createOpen, setCreateOpen] = useState(false);
  const [createdPlaintext, setCreatedPlaintext] = useState<{
    key: string;
    name: string | null;
  } | null>(null);

  return (
    <section data-testid="keys-page" className="flex flex-col gap-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h2 className="font-display text-display text-ink">
            {t('keys.title')}
          </h2>
          <p className="text-body text-ink-low">{t('keys.subtitle')}</p>
        </div>
        {canManageKeys ? (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            data-testid="keys-create-button"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-forest px-5 py-2.5 text-caption font-medium uppercase text-on-forest transition-all hover:bg-forest-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          >
            <Plus aria-hidden="true" className="h-4 w-4" />
            <span>{t('keys.create')}</span>
          </button>
        ) : null}
      </header>

      <div className="flex flex-col gap-3 rounded-xl bg-raised px-5 py-4 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <span className="font-mono text-mono-label uppercase text-ink-low">
            {t('keys.base_url.title')}
          </span>
          <p className="mt-1 text-caption text-ink-low">{t('keys.base_url.description')}</p>
          <code className="mt-2 block overflow-x-auto font-mono text-body text-forest">
            {getGatewayBaseUrl()}
          </code>
        </div>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(getGatewayBaseUrl());
            toast.success(t('keys.copied'));
          }}
          className="rounded p-1.5 text-ink-low hover:text-forest hover:bg-forest-tint transition-colors"
        >
          <Copy aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>

      <article className="flex flex-col gap-4 rounded-xl bg-card p-6">
        {keysQ.isLoading ? (
          <div className="flex flex-col gap-2" data-testid="keys-loading">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12" />
            ))}
            <span className="sr-only">{t('keys.loading')}</span>
          </div>
        ) : keysQ.isError ? (
          <p className="text-body text-bad" role="alert" data-testid="keys-error">
            {keysQ.error instanceof PlatformApiError
              ? `${keysQ.error.code}: ${keysQ.error.message}`
              : t('keys.error')}
          </p>
        ) : keysQ.data !== undefined && activeKeys.length === 0 ? (
          <p className="text-body text-ink-low" data-testid="keys-empty">
            {t('keys.empty')}
          </p>
        ) : keysQ.data !== undefined ? (
          <KeysTable keys={activeKeys} canManage={canManageKeys} />
        ) : null}
      </article>

      {createOpen && canManageKeys ? (
        <CreateKeyModal
          isPending={createM.isPending || applyNamespaceM.isPending}
          onClose={() => setCreateOpen(false)}
          onSubmit={async (name, monthlyLimitUsdCents, templateGroup) => {
            try {
              const body: CreateApiKeyRequest = {};
              if (name.length > 0) body.name = name;
              if (monthlyLimitUsdCents !== null) {
                body.monthly_limit_usd_cents = monthlyLimitUsdCents;
              }
              const result = await createM.mutateAsync(body);
              setCreateOpen(false);
              setCreatedPlaintext({ key: result.key, name: result.summary.name });
              if (templateGroup !== null) {
                try {
                  await applyNamespaceM.mutateAsync({
                    keyId: result.summary.id,
                    namespaceId: templateGroup.namespaceId,
                  });
                } catch (err) {
                  const message = err instanceof PlatformApiError
                    ? `${err.code}: ${err.message}`
                    : coerceErrorMessage(err);
                  toast.error(t('keys.template.failure', { error: message }));
                }
              }
              return null;
            } catch (err) {
              const message =
                err instanceof PlatformApiError
                  ? `${err.code}: ${err.message}`
                  : coerceErrorMessage(err);
              toast.error(message);
              return message;
            }
          }}
        />
      ) : null}

      {createdPlaintext !== null ? (
        <PlaintextReveal
          plaintext={createdPlaintext.key}
          keyName={createdPlaintext.name}
          onClose={() => setCreatedPlaintext(null)}
        />
      ) : null}
    </section>
  );
}

interface KeysTableProps {
  readonly keys: readonly ApiKeySummary[];
  readonly canManage: boolean;
}

function KeysTable({ keys, canManage }: KeysTableProps): JSX.Element {
  const t = useT();
  const displayRateQ = useDisplayRate();
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;
  const updateM = useUpdateApiKey();
  const revokeM = useRevokeApiKey();
  const toast = useToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftLimitCny, setDraftLimitCny] = useState('');
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeySummary | null>(null);
  const [mappingsTarget, setMappingsTarget] = useState<ApiKeySummary | null>(null);

  function startEdit(key: ApiKeySummary): void {
    setEditingId(key.id);
    setDraftName(key.name ?? '');
    setDraftLimitCny(formatLimitInputValue(key.monthlyLimitUsdCents, cnyPerUsd));
    setRowError(null);
  }

  async function saveEdit(key: ApiKeySummary): Promise<void> {
    setRowError(null);
    const name = draftName.trim();
    if (name.length === 0) {
      setRowError({ id: key.id, message: t('keys.row_error.name_required') });
      return;
    }
    const monthlyLimit = parseMonthlyLimitInput(draftLimitCny, cnyPerUsd, t);
    if (!monthlyLimit.ok) {
      setRowError({ id: key.id, message: monthlyLimit.message });
      return;
    }
    const body: UpdateApiKeyRequest = { name };
    if (
      isValidDisplayRate(cnyPerUsd) &&
      monthlyLimit.value !== key.monthlyLimitUsdCents
    ) {
      body.monthly_limit_usd_cents = monthlyLimit.value;
    }
    try {
      await updateM.mutateAsync({ id: key.id, body });
      setEditingId(null);
      setDraftName('');
      setDraftLimitCny('');
    } catch (err) {
      const message =
        err instanceof PlatformApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err, t('keys.row_error.network'));
      setRowError({ id: key.id, message });
      toast.error(message);
    }
  }

  return (
    <div className="overflow-x-auto" data-testid="keys-table">
      <table className="w-full min-w-[56rem] border-collapse text-body">
        <thead>
          <tr className="text-left text-[10px] uppercase text-ink-low">
            <th className="px-3 py-2 font-medium">{t('keys.column.prefix')}</th>
            <th className="px-3 py-2 font-medium">{t('keys.column.name')}</th>
            <th className="px-3 py-2 font-medium">{t('keys.column.monthly_limit')}</th>
            <th className="px-3 py-2 font-medium">{t('keys.column.created')}</th>
            <th className="px-3 py-2 font-medium">{t('keys.column.last_used')}</th>
            <th className="px-3 py-2 font-medium text-right">{t('keys.column.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((key) => {
            const isEditing = editingId === key.id;
            return (
              <tr
                key={key.id}
                data-testid={`keys-row-${key.id}`}
                className="odd:bg-raised"
              >
                <td className="px-3 py-2">
                  {key.prefix.length > 0 ? (
                    <span className="font-mono text-ink-mid">{key.prefix}…</span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-3 py-2 text-ink">
                  {isEditing ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="text"
                        value={draftName}
                        maxLength={120}
                        onChange={(e) => setDraftName(e.currentTarget.value)}
                        data-testid={`keys-edit-input-${key.id}`}
                        className={INPUT_CLASS}
                      />
                      <input
                        type="text"
                        inputMode="decimal"
                        value={draftLimitCny}
                        onChange={(e) => setDraftLimitCny(e.currentTarget.value)}
                        data-testid={`keys-edit-limit-${key.id}`}
                        placeholder={t('keys.limit_placeholder')}
                        className={`${INPUT_CLASS} w-32`}
                      />
                      <button
                        type="button"
                        onClick={() => void saveEdit(key)}
                        disabled={updateM.isPending}
                        data-testid={`keys-edit-save-${key.id}`}
                        className="inline-flex items-center justify-center rounded-lg bg-forest px-3 py-1 text-mono-label font-medium uppercase text-on-forest disabled:opacity-40 disabled:pointer-events-none"
                      >
                        {t('keys.save')}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingId(null);
                          setDraftName('');
                          setDraftLimitCny('');
                          setRowError(null);
                        }}
                        disabled={updateM.isPending}
                        data-testid={`keys-edit-cancel-${key.id}`}
                        className="inline-flex items-center justify-center rounded-lg bg-active px-3 py-1 text-mono-label font-medium uppercase text-ink disabled:opacity-40 disabled:pointer-events-none"
                      >
                        {t('keys.cancel')}
                      </button>
                    </div>
                  ) : (
                    <span data-testid={`keys-name-${key.id}`}>{key.name ?? '—'}</span>
                  )}
                  {rowError !== null && rowError.id === key.id ? (
                    <p
                      className="mt-1 text-mono-label font-medium text-bad"
                      role="alert"
                      data-testid={`keys-edit-error-${key.id}`}
                    >
                      {rowError.message}
                    </p>
                  ) : null}
                </td>
                <td
                  className="px-3 py-2 font-mono text-ink-mid"
                  data-testid={`keys-limit-${key.id}`}
                >
                  {key.monthlyLimitUsdCents === null
                    ? '—'
                    : formatUsdCentsAsCny(key.monthlyLimitUsdCents, cnyPerUsd)}
                </td>
                <td className="px-3 py-2 font-mono text-ink-mid">
                  {formatTimestamp(key.createdAt)}
                </td>
                <td className="px-3 py-2 font-mono text-ink-mid">
                  {key.lastUsedAt !== null ? formatTimestamp(key.lastUsedAt) : t('keys.never_used')}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setMappingsTarget(key)}
                      data-testid={`keys-mappings-${key.id}`}
                      className="inline-flex items-center justify-center rounded-lg bg-active px-3 py-1 text-mono-label font-medium uppercase text-ink transition-colors hover:bg-active/60 disabled:opacity-40 disabled:pointer-events-none"
                    >
                      {t('keys.mappings.button')}
                    </button>
                    {canManage && !isEditing ? (
                      <button
                        type="button"
                        onClick={() => startEdit(key)}
                        data-testid={`keys-rename-${key.id}`}
                        className="inline-flex items-center justify-center rounded-lg bg-active px-3 py-1 text-mono-label font-medium uppercase text-ink transition-colors hover:bg-active/60 disabled:opacity-40 disabled:pointer-events-none"
                      >
                        {t('keys.rename')}
                      </button>
                    ) : null}
                    {canManage ? (
                      <button
                        type="button"
                        onClick={() => setRevokeTarget(key)}
                        data-testid={`keys-revoke-${key.id}`}
                        className="inline-flex items-center justify-center rounded-lg bg-bad-tint px-3 py-1 text-mono-label font-medium uppercase text-bad transition-all hover:brightness-110 disabled:opacity-40 disabled:pointer-events-none"
                      >
                        {t('keys.revoke')}
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {mappingsTarget !== null ? (
        <KeyMappingsModal
          target={mappingsTarget}
          canManage={canManage}
          onClose={() => setMappingsTarget(null)}
        />
      ) : null}

      {revokeTarget !== null ? (
        <RevokeKeyModal
          target={revokeTarget}
          isPending={revokeM.isPending}
          onClose={() => setRevokeTarget(null)}
          onConfirm={async () => {
            try {
              await revokeM.mutateAsync(revokeTarget.id);
              setRevokeTarget(null);
              return null;
            } catch (err) {
              const message =
                err instanceof PlatformApiError
                  ? `${err.code}: ${err.message}`
                  : coerceErrorMessage(err);
              toast.error(message);
              return message;
            }
          }}
        />
      ) : null}
    </div>
  );
}

interface CreateKeyModalProps {
  readonly isPending: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (
    name: string,
    monthlyLimitUsdCents: number | null,
    templateGroup: SkuNamespaceGroup | null,
  ) => Promise<string | null>;
}

function CreateKeyModal({ isPending, onClose, onSubmit }: CreateKeyModalProps): JSX.Element {
  const t = useT();
  const [name, setName] = useState('');
  const [monthlyLimitCny, setMonthlyLimitCny] = useState('');
  const [templateNamespace, setTemplateNamespace] = useState('');
  const [error, setError] = useState<string | null>(null);
  const catalogQ = useCatalog();
  const displayRateQ = useDisplayRate();
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;

  const namespaceGroups = useMemo(
    () => groupSkusByNamespace(catalogQ.data?.skus ?? []),
    [catalogQ.data?.skus],
  );
  const selectedTemplateGroup =
    namespaceGroups.find((group) => group.namespaceId === templateNamespace) ?? null;

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const monthlyLimit = parseMonthlyLimitInput(monthlyLimitCny, cnyPerUsd, t);
    if (!monthlyLimit.ok) {
      setError(monthlyLimit.message);
      return;
    }
    const message = await onSubmit(name.trim(), monthlyLimit.value, selectedTemplateGroup);
    if (message !== null) {
      setError(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!isPending) onClose();
      }}
      titleId="create-key-modal-title"
      testId="keys-create-modal"
    >
      <form className="flex flex-col gap-6 p-8" onSubmit={handleSubmit} noValidate>
        <h2 id="create-key-modal-title" className="text-heading text-ink">
          {t('keys.create_modal.title')}
        </h2>
        <p className="text-body text-ink-low">{t('keys.create_modal.description')}</p>
        <label className="flex flex-col gap-2">
          <span className="font-mono text-mono-label uppercase text-ink-low">
            {t('keys.create_modal.name_label')}
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            disabled={isPending}
            maxLength={120}
            data-testid="keys-create-name"
            placeholder={t('keys.create_modal.name_placeholder')}
            className={INPUT_CLASS}
          />
        </label>
        <label className="flex flex-col gap-2">
          <span className="font-mono text-mono-label uppercase text-ink-low">
            {t('keys.create_modal.monthly_limit_label')}
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={monthlyLimitCny}
            onChange={(e) => {
              setMonthlyLimitCny(e.currentTarget.value);
              setError(null);
            }}
            disabled={isPending}
            data-testid="keys-create-monthly-limit"
            placeholder={t('keys.limit_placeholder')}
            className={INPUT_CLASS}
          />
          <span
            className="text-mono-label leading-4 text-ink-low"
            data-testid="keys-create-monthly-limit-hint"
          >
            {t('keys.create_modal.monthly_limit_hint')}
          </span>
        </label>
        <label className="flex flex-col gap-2">
          <span className="font-mono text-mono-label uppercase text-ink-low">
            {t('keys.create_modal.template_label')}
          </span>
          <select
            value={templateNamespace}
            onChange={(e) => {
              setTemplateNamespace(e.currentTarget.value);
              setError(null);
            }}
            disabled={isPending || catalogQ.isLoading || namespaceGroups.length === 0}
            data-testid="keys-create-template-namespace"
            className={INPUT_CLASS}
          >
            <option value="">{t('keys.create_modal.template_none')}</option>
            {namespaceGroups.map((group) => (
              <option key={group.namespaceId} value={group.namespaceId}>
                {group.displayName}
              </option>
            ))}
          </select>
          {selectedTemplateGroup !== null ? (
            <span
              className="text-mono-label leading-4 text-ink-low"
              data-testid="keys-create-template-hint"
            >
              {t('keys.create_modal.template_hint', {
                count: String(selectedTemplateGroup.skus.length),
              })}
            </span>
          ) : null}
        </label>
        {catalogQ.isError ? (
          <p className="text-caption text-ink-low" role="status">
            {t('keys.create_modal.catalog_error')}
          </p>
        ) : null}
        {error !== null ? (
          <p
            className="text-caption font-medium text-bad"
            role="alert"
            data-testid="keys-create-error"
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            data-testid="keys-create-cancel"
            className="inline-flex items-center justify-center rounded-lg bg-card px-4 py-2 text-caption font-medium uppercase text-ink transition-colors hover:bg-active/60 disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('keys.create_modal.cancel')}
          </button>
          <button
            type="submit"
            disabled={isPending}
            data-testid="keys-create-submit"
            className="inline-flex items-center justify-center rounded-lg bg-forest px-4 py-2 text-caption font-medium uppercase text-on-forest transition-all hover:bg-forest-bright disabled:opacity-40 disabled:pointer-events-none"
          >
            {isPending ? t('keys.create_modal.submitting') : t('keys.create_modal.submit')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

interface KeyMappingsModalProps {
  readonly target: ApiKeySummary;
  readonly canManage: boolean;
  readonly onClose: () => void;
}

interface NamespaceMappingPanelProps {
  readonly keyId: string;
  readonly canManage: boolean;
  readonly groups: readonly SkuNamespaceGroup[];
  readonly autoNamespaces: readonly ApiKeyAutoModelNamespaceRecord[];
  readonly catalogUnavailable: boolean;
  readonly namespaceOperationsEnabled: boolean;
}

function NamespaceMappingPanel({
  keyId,
  canManage,
  groups,
  autoNamespaces,
  catalogUnavailable,
  namespaceOperationsEnabled,
}: NamespaceMappingPanelProps): JSX.Element {
  const t = useT();
  const applyM = useApplyKeyModelNamespace();
  const enableAutoM = useEnableKeyAutoModelNamespace();
  const disableAutoM = useDisableKeyAutoModelNamespace();
  const [selectedNamespaceId, setSelectedNamespaceId] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [operationConflicts, setOperationConflicts] = useState<
    readonly ApiKeyModelMappingConflict[]
  >([]);
  const [disableTarget, setDisableTarget] = useState<ApiKeyAutoModelNamespaceRecord | null>(null);

  const pending = applyM.isPending || enableAutoM.isPending || disableAutoM.isPending;
  const selectedGroup = groups.find((group) => group.namespaceId === selectedNamespaceId) ?? null;
  const enabledIds = new Set(autoNamespaces.map((record) => record.namespace_id));

  function namespaceLabel(namespaceId: string, fallbackSlug: string | null): string {
    return groups.find((group) => group.namespaceId === namespaceId)?.displayName
      ?? fallbackSlug
      ?? namespaceId;
  }

  function skuLabel(skuId: string, fallbackName: string): string {
    for (const group of groups) {
      const sku = group.skus.find((candidate) => candidate.sku_id === skuId);
      if (sku !== undefined) return sku.sku_name;
    }
    return fallbackName;
  }

  function sourceLabel(source: 'manual' | 'automatic'): string {
    return source === 'automatic'
      ? t('keys.mappings.source.automatic')
      : t('keys.mappings.source.manual');
  }

  function errorMessage(err: unknown): string {
    return err instanceof PlatformApiError
      ? `${err.code}: ${err.message}`
      : coerceErrorMessage(err, t('keys.row_error.network'));
  }

  function handleOperationError(err: unknown): void {
    if (err instanceof ModelMappingConflictError) {
      setOperationConflicts(err.conflicts);
      setError(null);
      return;
    }
    setOperationConflicts([]);
    setError(errorMessage(err));
  }

  async function applyCurrentModels(): Promise<void> {
    if (selectedGroup === null) return;
    setError(null);
    setStatus(null);
    setOperationConflicts([]);
    try {
      const result = await applyM.mutateAsync({ keyId, namespaceId: selectedGroup.namespaceId });
      setStatus(t('keys.mappings.namespace.applied', {
        namespace: selectedGroup.displayName,
        count: String(result.applied_count),
      }));
    } catch (err) {
      handleOperationError(err);
    }
  }

  async function enableAutomaticMapping(): Promise<void> {
    if (selectedGroup === null) return;
    setError(null);
    setStatus(null);
    setOperationConflicts([]);
    try {
      await enableAutoM.mutateAsync({ keyId, namespaceId: selectedGroup.namespaceId });
      setStatus(t('keys.mappings.namespace.auto_enabled', { namespace: selectedGroup.displayName }));
    } catch (err) {
      handleOperationError(err);
    }
  }

  async function disableAutomaticMapping(record: ApiKeyAutoModelNamespaceRecord): Promise<void> {
    const label = namespaceLabel(record.namespace_id, record.namespace_slug);
    setError(null);
    setStatus(null);
    setOperationConflicts([]);
    try {
      await disableAutoM.mutateAsync({ keyId, namespaceId: record.namespace_id });
      setStatus(t('keys.mappings.namespace.auto_disabled', { namespace: label }));
      setDisableTarget(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <section
      aria-labelledby="keys-mapping-namespace-title"
      className="flex flex-col gap-4 rounded-lg border border-line bg-raised p-4"
      data-testid="keys-mapping-namespace-panel"
    >
      <div className="flex flex-col gap-1">
        <h3 id="keys-mapping-namespace-title" className="m-0 text-heading text-ink">
          {t('keys.mappings.namespace.title')}
        </h3>
        <p className="m-0 text-caption text-ink-low">
          {t('keys.mappings.namespace.description')}
        </p>
      </div>

      {canManage ? (
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-2">
            <span className="text-label font-medium text-ink-mid">
              {t('keys.mappings.namespace.select_label')}
            </span>
            <Select
              value={selectedNamespaceId}
              onValueChange={(value) => {
                setSelectedNamespaceId(value);
                setError(null);
                setStatus(null);
                setOperationConflicts([]);
              }}
              disabled={pending || catalogUnavailable || groups.length === 0}
              ariaLabel={t('keys.mappings.namespace.select_label')}
              testId="keys-mapping-namespace-action"
              placeholder={t('keys.mappings.namespace.select_placeholder')}
              options={groups.map((group) => ({
                value: group.namespaceId,
                label: group.displayName,
              }))}
            />
            {selectedGroup !== null ? (
              <span className="text-caption text-ink-low">
                {t('keys.mappings.namespace.selected_hint', {
                  count: String(selectedGroup.skus.length),
                })}
              </span>
            ) : null}
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              disabled={pending || selectedGroup === null || !namespaceOperationsEnabled}
              onClick={() => {
                void applyCurrentModels();
              }}
              data-testid="keys-mapping-namespace-apply"
            >
              {t('keys.mappings.namespace.apply')}
            </Button>
            <Button
              variant="primary"
              disabled={
                pending
                || selectedGroup === null
                || enabledIds.has(selectedNamespaceId)
                || !namespaceOperationsEnabled
              }
              onClick={() => {
                void enableAutomaticMapping();
              }}
              data-testid="keys-mapping-namespace-auto-enable"
            >
              {enabledIds.has(selectedNamespaceId)
                ? t('keys.mappings.namespace.auto_enabled_button')
                : t('keys.mappings.namespace.auto_enable')}
            </Button>
          </div>
        </div>
      ) : (
        <p role="status" className="m-0 text-caption text-ink-mid" data-testid="keys-mappings-readonly">
          {t('keys.mappings.readonly')}
        </p>
      )}

      {!namespaceOperationsEnabled ? (
        <p
          role="status"
          className="m-0 rounded border border-warn-line bg-warn-tint px-3 py-2 text-caption text-ink-mid"
          data-testid="keys-mapping-namespace-operations-disabled"
        >
          {t('keys.mappings.namespace.operations_disabled')}
        </p>
      ) : null}

      {operationConflicts.length > 0 ? (
        <section
          role="alert"
          className="flex flex-col gap-2 rounded border border-warn-line bg-warn-tint px-3 py-2"
          data-testid="keys-mapping-namespace-conflicts"
        >
          <p className="m-0 text-label font-medium text-warn">
            {t('keys.mappings.conflicts.title')}
          </p>
          <p className="m-0 text-caption text-ink-mid">
            {t('keys.mappings.conflicts.description')}
          </p>
          <ul className="m-0 list-none space-y-2 p-0">
            {operationConflicts.map((conflict) => (
              <li
                key={conflict.alias}
                className="rounded border border-warn-line bg-card px-3 py-2"
                data-testid={`keys-mapping-namespace-conflict-${conflict.alias}`}
              >
                <p className="m-0 font-mono text-mono text-ink">{conflict.alias}</p>
                <ul className="m-0 mt-1 list-disc space-y-1 pl-5 text-caption text-ink-mid">
                  {conflict.candidates.map((candidate) => (
                    <li key={`${candidate.namespace_id}:${candidate.sku_id}`}>
                      <span className="font-mono">
                        {skuLabel(candidate.sku_id, candidate.sku_name)}
                      </span>
                      {' · '}
                      {candidate.sources.map(sourceLabel).join(', ')}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="flex flex-col gap-2">
        <p className="m-0 text-label font-medium text-ink-mid">
          {t('keys.mappings.namespace.enabled_title')}
        </p>
        {autoNamespaces.length === 0 ? (
          <p className="m-0 text-caption text-ink-low" data-testid="keys-mapping-auto-empty">
            {t('keys.mappings.namespace.enabled_empty')}
          </p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0" data-testid="keys-mapping-auto-list">
            {autoNamespaces.map((record) => (
              <li
                key={record.namespace_id}
                className="flex flex-wrap items-center justify-between gap-2 rounded border border-line-soft bg-card px-3 py-2"
                data-testid={`keys-mapping-auto-${record.namespace_id}`}
              >
                <span className="font-mono text-mono text-ink">
                  {namespaceLabel(record.namespace_id, record.namespace_slug)}
                </span>
                {canManage ? (
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={pending}
                    onClick={() => setDisableTarget(record)}
                    data-testid={`keys-mapping-auto-disable-${record.namespace_id}`}
                  >
                    {t('keys.mappings.namespace.auto_disable')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {catalogUnavailable ? (
        <p role="alert" className="m-0 text-caption text-bad">
          {t('keys.mappings.catalog_error')}
        </p>
      ) : null}
      {error !== null ? (
        <p role="alert" className="m-0 text-caption text-bad" data-testid="keys-mapping-namespace-error">
          {error}
        </p>
      ) : null}
      {status !== null ? (
        <p role="status" className="m-0 text-caption text-ok" data-testid="keys-mapping-namespace-status">
          {status}
        </p>
      ) : null}

      {disableTarget !== null ? (
        <Modal
          isOpen
          onClose={() => {
            if (!disableAutoM.isPending) setDisableTarget(null);
          }}
          titleId="disable-auto-namespace-title"
          testId="keys-mapping-auto-disable-confirm"
        >
          <div className="flex flex-col gap-5 p-6">
            <div className="flex flex-col gap-2">
              <h2 id="disable-auto-namespace-title" className="m-0 text-heading text-ink">
                {t('keys.mappings.namespace.disable_title')}
              </h2>
              <p className="m-0 text-body text-ink-mid">
                {t('keys.mappings.namespace.disable_description', {
                  namespace: namespaceLabel(disableTarget.namespace_id, disableTarget.namespace_slug),
                })}
              </p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="secondary"
                disabled={disableAutoM.isPending}
                onClick={() => setDisableTarget(null)}
              >
                {t('keys.mappings.namespace.disable_cancel')}
              </Button>
              <Button
                variant="danger"
                disabled={disableAutoM.isPending}
                onClick={() => {
                  void disableAutomaticMapping(disableTarget);
                }}
                data-testid="keys-mapping-auto-disable-confirm-confirm"
              >
                {t('keys.mappings.namespace.disable_confirm')}
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}

/**
 * Per-key "Mappings" drawer (epic #898 P6 #913). Lists the key's
 * alias → SKU mappings, adds new ones (alias input + a namespace-grouped
 * SKU select fed by `/v1/catalog/me` `skus[]`), re-PUTs an existing
 * alias to change its target, and deletes.
 *
 * Alias validation renders inline next to the input — the platform
 * rejects aliases containing `/`, so the same rule is checked
 * client-side and any server-side alias rejection also lands on the
 * field, never toast-only.
 */
function KeyMappingsModal({ target, canManage, onClose }: KeyMappingsModalProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const mappingsQ = useKeyModelMappings(target.id);
  const catalogQ = useCatalog();
  const upsertM = useUpsertKeyModelMapping();
  const deleteM = useDeleteKeyModelMapping();

  const [editingAlias, setEditingAlias] = useState<string | null>(null);
  const [alias, setAlias] = useState('');
  const [skuId, setSkuId] = useState('');
  const [aliasError, setAliasError] = useState<string | null>(null);
  const [skuError, setSkuError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [mutationConflicts, setMutationConflicts] = useState<
    readonly ApiKeyModelMappingConflict[]
  >([]);
  const [deleteConflictAlias, setDeleteConflictAlias] = useState<string | null>(null);

  const pending = upsertM.isPending || deleteM.isPending;
  const mappings = mappingsQ.data?.mappings ?? [];
  const autoNamespaces = mappingsQ.data?.auto_namespaces ?? [];
  const conflicts = mappingsQ.data?.conflicts ?? [];
  const displayedConflicts = [
    ...conflicts.filter((conflict) =>
      !mutationConflicts.some((candidate) => candidate.alias === conflict.alias)),
    ...mutationConflicts,
  ];
  const skus = useMemo(() => catalogQ.data?.skus ?? [], [catalogQ.data?.skus]);
  const namespaceGroups = useMemo(() => groupSkusByNamespace(skus), [skus]);
  const skuById = useMemo(() => {
    const map = new Map<string, CatalogSku>();
    for (const sku of skus) map.set(sku.sku_id, sku);
    return map;
  }, [skus]);

  function resetForm(): void {
    setEditingAlias(null);
    setAlias('');
    setSkuId('');
    setAliasError(null);
    setSkuError(null);
    setMutationConflicts([]);
  }

  function startEdit(mapping: ApiKeyModelMappingRecord): void {
    setEditingAlias(mapping.alias);
    setAlias(mapping.alias);
    setAliasError(null);
    setSkuError(null);
    setListError(null);
    setMutationConflicts([]);
    setSkuId(skuById.has(mapping.sku_id) ? mapping.sku_id : '');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setAliasError(null);
    setSkuError(null);
    setListError(null);

    const trimmedAlias = alias.trim();
    if (trimmedAlias.length === 0) {
      setAliasError(t('keys.mappings.alias_required'));
      return;
    }
    if (trimmedAlias.includes('/')) {
      setAliasError(t('keys.mappings.alias_no_slash'));
      return;
    }
    if (skuId.length === 0) {
      setSkuError(t('keys.mappings.sku_required'));
      return;
    }
    const isEdit = editingAlias !== null;
    setMutationConflicts([]);
    try {
      await upsertM.mutateAsync({ keyId: target.id, alias: trimmedAlias, skuId });
      toast.success(isEdit ? t('keys.mappings.toast.updated') : t('keys.mappings.toast.added'));
      resetForm();
    } catch (err) {
      if (err instanceof ModelMappingConflictError) {
        setMutationConflicts(err.conflicts);
        setAliasError(null);
        return;
      }
      // The platform's alias validation (no `/`, length) belongs to the
      // alias field; render it inline there instead of toast-only.
      const message =
        err instanceof PlatformApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err, t('keys.row_error.network'));
      setAliasError(message);
    }
  }

  async function handleDeleteAlias(aliasToDelete: string): Promise<void> {
    setListError(null);
    try {
      await deleteM.mutateAsync({ keyId: target.id, alias: aliasToDelete });
      toast.success(t('keys.mappings.toast.deleted'));
      setMutationConflicts((current) =>
        current.filter((conflict) => conflict.alias !== aliasToDelete));
      if (editingAlias === aliasToDelete) resetForm();
      if (deleteConflictAlias === aliasToDelete) setDeleteConflictAlias(null);
    } catch (err) {
      const message =
        err instanceof PlatformApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err, t('keys.row_error.network'));
      setListError(message);
    }
  }

  function mappingTargetLabel(mapping: ApiKeyModelMappingRecord): string {
    // Catalog `sku_name` is brand-aliased for the Nxtpath brand; the
    // wire `sku_name` (canonical) is only a fallback for SKUs that have
    // left the org-visible catalog.
    return skuById.get(mapping.sku_id)?.sku_name ?? mapping.sku_name;
  }

  function sourceLabel(source: ApiKeyModelMappingRecord['source'] | undefined): string {
    return source === 'automatic'
      ? t('keys.mappings.source.automatic')
      : t('keys.mappings.source.manual');
  }

  function mappingSources(mapping: ApiKeyModelMappingRecord): readonly ApiKeyModelMappingRecord['source'][] {
    return mapping.sources?.length > 0 ? mapping.sources : [mapping.source ?? 'manual'];
  }

  function isManagedByAuto(mapping: ApiKeyModelMappingRecord): boolean {
    return mapping.managed_by_auto ?? mappingSources(mapping).includes('automatic');
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!pending) onClose();
      }}
      titleId="key-mappings-title"
      testId="keys-mappings-modal"
      variant="drawer"
    >
      <div className="flex h-full flex-col bg-card">
        <header className="border-b border-line px-6 py-5">
          <span className="font-mono text-mono-label uppercase text-rust-text">
            {t('keys.mappings.eyebrow')}
          </span>
          <h2 id="key-mappings-title" className="m-0 mt-1 font-display text-title text-ink">
            {t('keys.mappings.title')}
          </h2>
          <p className="m-0 mt-1 text-caption text-ink-low">
            {t('keys.mappings.subtitle', {
              key: target.name ?? (target.prefix.length > 0 ? `${target.prefix}…` : target.id),
            })}
          </p>
        </header>

        <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4">
          {mappingsQ.isLoading ? (
            <div className="flex flex-col gap-2" data-testid="keys-mappings-loading">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
              <span className="sr-only">{t('keys.mappings.loading')}</span>
            </div>
          ) : mappingsQ.isError ? (
            <div
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-bad-line bg-bad-tint px-4 py-3"
              role="alert"
              data-testid="keys-mappings-load-error"
            >
              <span className="text-body text-bad">
                {mappingsQ.error instanceof PlatformApiError
                  ? `${mappingsQ.error.code}: ${mappingsQ.error.message}`
                  : t('keys.mappings.error')}
              </span>
              <button
                type="button"
                onClick={() => {
                  void mappingsQ.refetch();
                }}
                className="inline-flex items-center justify-center rounded-lg bg-active px-3 py-1 text-mono-label font-medium uppercase text-ink"
              >
                {t('keys.mappings.retry')}
              </button>
            </div>
          ) : (
            <>
              <NamespaceMappingPanel
                keyId={target.id}
                canManage={canManage}
                groups={namespaceGroups}
                autoNamespaces={autoNamespaces}
                catalogUnavailable={catalogQ.isError}
                namespaceOperationsEnabled={
                  mappingsQ.data?.namespace_operations_enabled !== false
                }
              />

              {displayedConflicts.length > 0 ? (
                <section
                  role="alert"
                  className="flex flex-col gap-3 rounded-lg border border-warn-line bg-warn-tint p-4"
                  data-testid="keys-mapping-conflicts"
                >
                  <div className="flex flex-col gap-1">
                    <h3 className="m-0 text-heading text-warn">
                      {t('keys.mappings.conflicts.title')}
                    </h3>
                    <p className="m-0 text-caption text-ink-mid">
                      {t('keys.mappings.conflicts.description')}
                    </p>
                  </div>
                  <ul className="m-0 flex list-none flex-col gap-2 p-0">
                    {displayedConflicts.map((conflict) => {
                      const isTransient = mutationConflicts.some((candidate) =>
                        candidate.alias === conflict.alias);
                      const hasPersistedManual = !isTransient && conflict.candidates.some(
                        (candidate) => candidate.sources.includes('manual'),
                      );
                      return (
                        <li
                          key={conflict.alias}
                          className="rounded border border-warn-line bg-card px-3 py-2"
                          data-testid={`keys-mapping-conflict-${conflict.alias}`}
                        >
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <p className="m-0 font-mono text-mono text-ink">{conflict.alias}</p>
                              <ul className="m-0 mt-1 list-disc space-y-1 pl-5 text-caption text-ink-mid">
                                {conflict.candidates.map((candidate) => (
                                  <li key={candidate.sku_id}>
                                    <span className="font-mono">
                                      {skuById.get(candidate.sku_id)?.sku_name ?? candidate.sku_name}
                                    </span>
                                    {' · '}
                                    {candidate.sources.map((source) => sourceLabel(source)).join(', ')}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            {canManage && hasPersistedManual ? (
                              <Button
                                variant="danger"
                                size="sm"
                                disabled={pending}
                                onClick={() => setDeleteConflictAlias(conflict.alias)}
                                data-testid={`keys-mapping-conflict-delete-${conflict.alias}`}
                              >
                                {t('keys.mappings.conflicts.delete_manual')}
                              </Button>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ) : null}

              {mappings.length === 0 ? (
                <p
                  className="m-0 rounded-lg border border-line bg-raised px-4 py-6 text-body text-ink-low"
                  data-testid="keys-mappings-empty"
                >
                  {t('keys.mappings.empty')}
                </p>
              ) : (
                <Table testId="keys-mappings-table">
                  <THead>
                    <Tr interactive={false}>
                      <Th>{t('keys.mappings.column.alias')}</Th>
                      <Th>{t('keys.mappings.column.target')}</Th>
                      <Th>{t('keys.mappings.column.source')}</Th>
                      {canManage ? <Th>{t('keys.column.actions')}</Th> : null}
                    </Tr>
                  </THead>
                  <TBody>
                    {mappings.map((mapping) => {
                      const automatic = isManagedByAuto(mapping);
                      return (
                        <Tr key={mapping.alias} data-testid={`keys-mapping-row-${mapping.alias}`}>
                          <Td mono emphasis heading>{mapping.alias}</Td>
                          <Td
                            mono
                            label={t('keys.mappings.column.target')}
                            data-testid={`keys-mapping-target-${mapping.alias}`}
                          >
                            {mappingTargetLabel(mapping)}
                          </Td>
                          <Td
                            label={t('keys.mappings.column.source')}
                            data-testid={`keys-mapping-source-${mapping.alias}`}
                          >
                            <span className={`inline-flex rounded-full border px-2 py-0.5 font-mono text-mono-label ${automatic
                              ? 'border-ok-line bg-ok-tint text-ok'
                              : 'border-line bg-well text-ink-mid'}`}>
                              {mappingSources(mapping).map((source) => sourceLabel(source)).join(' + ')}
                            </span>
                          </Td>
                          {canManage ? (
                            <Td label={t('keys.column.actions')} stack>
                              {automatic ? (
                                <span className="text-caption text-ink-low">
                                  {t('keys.mappings.automatic_managed')}
                                </span>
                              ) : (
                                <div className="flex flex-wrap items-center justify-end gap-2 max-md:justify-start">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    disabled={pending}
                                    onClick={() => startEdit(mapping)}
                                    data-testid={`keys-mapping-edit-${mapping.alias}`}
                                  >
                                    {t('keys.mappings.edit')}
                                  </Button>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    disabled={pending}
                                    onClick={() => {
                                      void handleDeleteAlias(mapping.alias);
                                    }}
                                    data-testid={`keys-mapping-delete-${mapping.alias}`}
                                  >
                                    {t('keys.mappings.delete')}
                                  </Button>
                                </div>
                              )}
                            </Td>
                          ) : null}
                        </Tr>
                      );
                    })}
                  </TBody>
                </Table>
              )}
            </>
          )}

          {listError !== null ? (
            <p
              className="mt-3 text-caption font-medium text-bad"
              role="alert"
              data-testid="keys-mappings-list-error"
            >
              {listError}
            </p>
          ) : null}

          {canManage && !mappingsQ.isLoading && !mappingsQ.isError ? (
            <form
              noValidate
              onSubmit={(event) => {
                void handleSubmit(event);
              }}
              className="mt-6 flex flex-col gap-4 rounded-lg border border-line bg-raised p-4"
              data-testid="keys-mapping-form"
            >
              <p className="m-0 font-mono text-mono-label uppercase text-ink-low">
                {editingAlias !== null
                  ? t('keys.mappings.edit_title', { alias: editingAlias })
                  : t('keys.mappings.add_title')}
              </p>
              <label className="flex flex-col gap-2">
                <span className="font-mono text-mono-label uppercase text-ink-low">
                  {t('keys.mappings.alias_label')}
                </span>
                <input
                  type="text"
                  value={alias}
                  maxLength={200}
                  disabled={pending || editingAlias !== null}
                  aria-invalid={aliasError !== null || undefined}
                  placeholder={t('keys.mappings.alias_placeholder')}
                  onChange={(event) => {
                    setAlias(event.currentTarget.value);
                    setAliasError(null);
                  }}
                  data-testid="keys-mapping-alias"
                  className={INPUT_CLASS}
                />
                {aliasError !== null ? (
                  <span
                    className="text-mono-label font-medium leading-4 text-bad"
                    role="alert"
                    data-testid="keys-mapping-alias-error"
                  >
                    {aliasError}
                  </span>
                ) : (
                  <span className="text-mono-label leading-4 text-ink-low">
                    {t('keys.mappings.alias_hint')}
                  </span>
                )}
              </label>
              <label className="flex flex-col gap-2">
                <span className="font-mono text-mono-label uppercase text-ink-low">
                  {t('keys.mappings.sku_label')}
                </span>
                <select
                  value={skuId}
                  onChange={(event) => {
                    setSkuId(event.currentTarget.value);
                    setSkuError(null);
                  }}
                  disabled={pending || catalogQ.isLoading || catalogQ.isError}
                  data-testid="keys-mapping-sku"
                  className={INPUT_CLASS}
                >
                  <option value="">{t('keys.mappings.sku_placeholder')}</option>
                  {namespaceGroups.map((group) => (
                    <optgroup key={group.namespaceId} label={group.displayName}>
                      {group.skus.map((sku) => (
                        <option key={sku.sku_id} value={sku.sku_id}>
                          {sku.model_display_name ?? sku.model}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {skuError !== null ? (
                  <span
                    className="text-mono-label font-medium leading-4 text-bad"
                    role="alert"
                    data-testid="keys-mapping-sku-error"
                  >
                    {skuError}
                  </span>
                ) : null}
              </label>
              {catalogQ.isError ? (
                <p className="m-0 text-caption font-medium text-bad" role="alert">
                  {t('keys.mappings.catalog_error')}
                </p>
              ) : null}
              <div className="flex items-center justify-end gap-3">
                {editingAlias !== null ? (
                  <button
                    type="button"
                    onClick={resetForm}
                    disabled={pending}
                    data-testid="keys-mapping-cancel-edit"
                    className="inline-flex items-center justify-center rounded-lg bg-card px-4 py-2 text-caption font-medium uppercase text-ink transition-colors hover:bg-active/60 disabled:opacity-40 disabled:pointer-events-none"
                  >
                    {t('keys.mappings.cancel_edit')}
                  </button>
                ) : null}
                <button
                  type="submit"
                  disabled={pending || catalogQ.isLoading}
                  data-testid="keys-mapping-submit"
                  className="inline-flex items-center justify-center rounded-lg bg-forest px-4 py-2 text-caption font-medium uppercase text-on-forest transition-all hover:bg-forest-bright disabled:opacity-40 disabled:pointer-events-none"
                >
                  {editingAlias !== null
                    ? t('keys.mappings.submit_save')
                    : t('keys.mappings.submit_add')}
                </button>
              </div>
            </form>
          ) : null}

          {deleteConflictAlias !== null ? (
            <Modal
              isOpen
              onClose={() => {
                if (!deleteM.isPending) setDeleteConflictAlias(null);
              }}
              titleId="delete-conflict-mapping-title"
              testId="keys-mapping-conflict-delete-confirm"
            >
              <div className="flex flex-col gap-5 p-6">
                <div className="flex flex-col gap-2">
                  <h2 id="delete-conflict-mapping-title" className="m-0 text-heading text-ink">
                    {t('keys.mappings.conflicts.delete_title')}
                  </h2>
                  <p className="m-0 text-body text-ink-mid">
                    {t('keys.mappings.conflicts.delete_description', { alias: deleteConflictAlias })}
                  </p>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    variant="secondary"
                    disabled={deleteM.isPending}
                    onClick={() => setDeleteConflictAlias(null)}
                  >
                    {t('keys.mappings.conflicts.delete_cancel')}
                  </Button>
                  <Button
                    variant="danger"
                    disabled={deleteM.isPending}
                    onClick={() => {
                      void handleDeleteAlias(deleteConflictAlias);
                    }}
                    data-testid="keys-mapping-conflict-delete-confirm-confirm"
                  >
                    {t('keys.mappings.conflicts.delete_confirm')}
                  </Button>
                </div>
              </div>
            </Modal>
          ) : null}
        </main>

        <footer className="flex items-center justify-end gap-3 border-t border-line px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            data-testid="keys-mappings-close"
            className="inline-flex items-center justify-center rounded-lg bg-card px-4 py-2 text-caption font-medium uppercase text-ink transition-colors hover:bg-active/60 disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('keys.mappings.close')}
          </button>
        </footer>
      </div>
    </Modal>
  );
}

interface PlaintextRevealProps {
  readonly plaintext: string;
  readonly keyName: string | null;
  readonly onClose: () => void;
}

function PlaintextReveal({ plaintext, keyName, onClose }: PlaintextRevealProps): JSX.Element {
  const t = useT();
  const { copy, copied, error } = useClipboard();
  const [hasCopied, setHasCopied] = useState(false);

  const handleCopy = async (): Promise<void> => {
    const ok = await copy(plaintext);
    if (ok) {
      setHasCopied(true);
    }
  };

  const handlePrimaryAction = (): void => {
    if (hasCopied) {
      onClose();
      return;
    }
    void handleCopy();
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      titleId="plaintext-reveal-title"
      testId="keys-plaintext-modal"
      closeOnBackdrop={false}
    >
      <div className="flex flex-col gap-6 p-8">
        <h2 id="plaintext-reveal-title" className="text-heading text-ink">
          {t('keys.plaintext.title')}
        </h2>
        <p
          className="rounded-lg border border-warn-line bg-warn-tint px-4 py-3 text-body font-medium text-ink"
          data-testid="keys-plaintext-warning"
          role="alert"
        >
          {t('keys.plaintext.warning')}
        </p>
        <button
          type="button"
          onClick={() => void handleCopy()}
          data-testid="keys-plaintext-copy"
          aria-label={t('keys.plaintext.copy_aria_label')}
          className="group flex flex-col items-start gap-2 rounded-lg bg-well p-4 text-left transition-all hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
        >
          <span className="text-[10px] uppercase text-ink-low">
            {keyName !== null && keyName.length > 0
              ? t('keys.plaintext.hint.click_with_name', { name: keyName })
              : t('keys.plaintext.hint.click_to_copy')}
          </span>
          <span
            data-testid="keys-plaintext-value"
            className="w-full break-all font-mono text-lg font-medium text-forest select-all"
          >
            {plaintext}
          </span>
          <span
            data-testid="keys-plaintext-copy-status"
            className={`text-[10px] uppercase ${
              copied ? 'text-rust-text' : 'text-ink-low'
            }`}
          >
            {copied ? t('keys.plaintext.copied') : t('keys.plaintext.copy_anywhere')}
          </span>
        </button>
        {error !== null ? (
          <p
            className="text-mono-label font-medium text-bad"
            role="alert"
            data-testid="keys-plaintext-copy-error"
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="keys-plaintext-close"
            className="inline-flex items-center justify-center rounded-lg bg-card px-4 py-2 text-caption font-medium uppercase text-ink transition-colors hover:bg-active"
          >
            {t('keys.plaintext.close')}
          </button>
          <button
            type="button"
            onClick={handlePrimaryAction}
            data-testid="keys-plaintext-done"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-forest px-4 py-2 text-caption font-medium uppercase text-on-forest transition-all hover:bg-forest-bright"
          >
            <span aria-hidden="true" className="inline-flex">
              {hasCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </span>
            <span>{hasCopied ? t('keys.plaintext.done') : t('keys.plaintext.copy_button')}</span>
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface RevokeKeyModalProps {
  readonly target: ApiKeySummary;
  readonly isPending: boolean;
  readonly onClose: () => void;
  readonly onConfirm: () => Promise<string | null>;
}

function RevokeKeyModal({
  target,
  isPending,
  onClose,
  onConfirm,
}: RevokeKeyModalProps): JSX.Element {
  const t = useT();
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm(): Promise<void> {
    setError(null);
    const message = await onConfirm();
    if (message !== null) {
      setError(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!isPending) onClose();
      }}
      titleId="revoke-key-modal-title"
      testId="keys-revoke-modal"
    >
      <div className="flex flex-col gap-6 p-8">
        <h2 id="revoke-key-modal-title" className="text-heading text-ink">
          {t('keys.revoke_modal.title')}
        </h2>
        <p className="text-body text-ink-low">
          {t('keys.revoke_modal.description.before')}
          <span className="font-mono font-medium text-ink">
            {target.prefix.length > 0 ? `${target.prefix}…` : target.id}
          </span>
          {t('keys.revoke_modal.description.after')}
        </p>
        {error !== null ? (
          <p
            className="text-caption font-medium text-bad"
            role="alert"
            data-testid="keys-revoke-error"
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            data-testid="keys-revoke-cancel"
            className="inline-flex items-center justify-center rounded-lg bg-card px-4 py-2 text-caption font-medium uppercase text-ink transition-colors hover:bg-active/60 disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('keys.revoke_modal.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={isPending}
            data-testid="keys-revoke-confirm"
            className="inline-flex items-center justify-center rounded-lg bg-bad-tint px-4 py-2 text-caption font-medium uppercase text-bad transition-all hover:brightness-110 disabled:opacity-40 disabled:pointer-events-none"
          >
            {isPending ? t('keys.revoke_modal.submitting') : t('keys.revoke_modal.submit')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
